import React, { useCallback, useEffect, useState } from 'react';
import axiosPrivate from 'config/axiosPrivate';

import { useDispatch, useSelector } from 'react-redux';
import { addUser, updateUser } from 'store/AdminClient/CLientAdminManagement';

import { CustomChipsAutocomplete } from 'components/shared/DropDown/CustomChipsAutocomplete';
import { CustomAutocompleteMultipleChoice } from 'components/shared/DropDown/CustomAutocompleteMultipleChoice';
import { PWD_REGEX, EMAIL_REGEX, PHONENUMBER_REGEX } from 'utils/Regex';
import { LANGUAGES, ROLES } from 'utils/Constants';
import { lower } from 'utils/StringUtils';

import ErrorSuccesModal from 'components/shared/modal/ErrorSuccesModal';
import { Modal } from 'reactstrap';

import { classes } from 'components/shared/modal/modalClasses';
import { disabledInput } from 'components/shared/disabledClasses';
import { UpDownInputArrows } from 'components/shared/UpDownInputArrows';

import { MAIN_LANGUAGES } from 'utils/Constants';
import { useTranslation } from 'react-i18next';
import { UserLanguageSelector } from '../UserLanguageSelector';
import { LanguageSelector } from '../LanguageSelector';
import { UserSessionSelector } from 'store/UserSession';

import './UmModal.css';

const API = {
  createWriter: '/user/external',
  updateWriter: '/user/external',
  getAvailableThematics: '/user/thematic/all',
  getAvailableLanguages: '/user/language/all',
};

export const GlobalWriterModal = (props) => {
  const { user: currentUser } = useSelector(UserSessionSelector);
  const dispatch = useDispatch();
  const { t } = useTranslation();
  const [mail, setMail] = useState('');
  const [name, setName] = useState('');
  const [lastName, setLastName] = useState('');
  const [phoneNumber, setPhoneNumber] = useState('');
  const [password, setPassword] = useState('');
  const [showPassword, setShowPassword] = useState('');
  const [minRate, setMinRate] = useState(0);
  const [maxRate, setMaxRate] = useState(0);

  const [selectedLanguage, setSelectedLanguage] = useState(null);

  const [limit, setLimit] = useState(0);

  const [address, setAddress] = useState('');
  const [companyName, setCompanyName] = useState('');

  const [availableThematicsList, setAvailableThematicsList] = useState([]);
  const [availableLanguagesList, setAvailableLanguagesList] = useState([]);

  const [mainWriterLanguages, setMainWriterLanguages] = useState([]);
  const [minorWriterLanguages, setMinorWriterLanguages] = useState([]);
  const [writerThematics, setWriterThematics] = useState([]);

  const [errorSuccess, setErrorSuccess] = useState(false);
  const [errorSuccessMsg, setErrorSuccessMsg] = useState('');
  const [isSuccess, setIsSuccess] = useState(false);

  const [disabledForm, setDisabledForm] = useState(true);

  useEffect(() => {
    axiosPrivate
      .get(API.getAvailableLanguages)
      .then((res) => setAvailableLanguagesList(res.data));

    axiosPrivate
      .get(API.getAvailableThematics)
      .then((res) => setAvailableThematicsList(res.data));
  }, []);

  useEffect(() => {
    if (props && props.user) {
      setMail(props.user.mail);
      setName(props.user.firstName);
      setLastName(props.user.lastName);

      setPhoneNumber(props.user.phoneNumber);
      setCompanyName(props.user.companyName || currentUser?.companyName || '');
      setAddress(props.user.address);
      setLimit(props.user.limit);
      setMinRate(props.user.minRate);
      setMaxRate(props.user.maxRate);
      setWriterThematics(
        Array.isArray(props.user.thematics) ? props.user.thematics : []
      );
      const userLanguages = Array.isArray(props.user.languages)
        ? props.user.languages
        : [];

      setMainWriterLanguages(
        userLanguages.filter((x) => MAIN_LANGUAGES.includes(x.language))
      );
      setMinorWriterLanguages(
        userLanguages.filter((x) => !MAIN_LANGUAGES.includes(x.language))
      );

      const lang = LANGUAGES.find(
        (l) => l.isoCode === props.user.isoLanguageCode
      );
      setSelectedLanguage(lang || null);
    }
  }, [props.user, currentUser?.companyName]);

  const checkPasswordValid = useCallback(() => {
    return !password || PWD_REGEX.test(password);
  }, [password]);

  const checkValidForm = useCallback(() => {

    if (!mail || !name || !lastName || !selectedLanguage) {
      setDisabledForm(true);
      return;
    }

    if (mail.length > 0 && !mail.match(EMAIL_REGEX)) {
      setDisabledForm(true);
      return;
    }


    return setDisabledForm(!checkPasswordValid());
  }, [
    checkPasswordValid,
    mail,
    name,
    lastName,
    selectedLanguage,
  ]);

  useEffect(() => {
    checkValidForm();
  }, [
    mail,
    name,
    lastName,
    password,
    phoneNumber,
    companyName,
    address,
    writerThematics,
    mainWriterLanguages,
    minorWriterLanguages,
    selectedLanguage,
    checkValidForm,
  ]);

  const callbackFromThematicMultiSelect = (newList) => {
    setWriterThematics(newList);
  };

  const callbackFromLanguagesMultiSelect = (newList) => {
    setMinorWriterLanguages(newList);
  };

  const findLanguageIndex = (language) => {
    return mainWriterLanguages.findIndex((element) =>
      lower(element.language).includes(lower(language))
    );
  };

  const checkLanguage = (language) => {
    let index = findLanguageIndex(language);
    let array = [...mainWriterLanguages];
    if (index > -1) {
      array.splice(index, 1);
    } else {
      let item = availableLanguagesList.find((element) =>
        lower(element.language).includes(lower(language))
      );
      array.push(item);
    }

    setMainWriterLanguages(array);
  };

  const updateWriter = () => {
    const inheritedCompanyName = companyName?.trim() || currentUser?.companyName || '';
    let body = {
      password: !password ? null : password,
      mail,
      firstName: name,
      lastName: lastName,
      phoneNumber,
      companyName: inheritedCompanyName,
      address,
      limit,
      minRate: parseFloat(minRate),
      maxRate: parseFloat(maxRate),
      languages: mainWriterLanguages.concat(minorWriterLanguages),
      thematics: writerThematics,
      role: ROLES.GLOBAL_WRITER,
      isoLanguageCode: selectedLanguage.isoCode,
    };

    axiosPrivate
      .put(API.updateWriter, body, { params: { mail: props.user.mail } })
      .then((res) => {
        dispatch(updateUser({
          ...props.user,
          ...res.data,
          firstName: name,
          lastName: lastName,
          role: ROLES.GLOBAL_WRITER,
          mail: mail,
          _originalMail: props.user.mail,
        }));
        setIsSuccess(true);
        setErrorSuccessMsg(
          `${t('Le rédacteur')} ${props.user.mail} ${t(
            'a été mis à jour avec succès'
          )} !`
        );

        setErrorSuccess(true);
      })
      .catch((e) => {
        setIsSuccess(false);
        switch (e.response.status) {
          case 409:
            setErrorSuccessMsg(
              `${t("Un utilisateur existe déjà avec l'email")} ${mail}`
            );
            break;
          default:
            setErrorSuccessMsg(t("Oups....! Une erreur s'est produite"));
            break;
        }
        setErrorSuccess(true);
      });
  };

  const createWriter = () => {
    const inheritedCompanyName = companyName?.trim() || currentUser?.companyName || '';
    let body = {
      mail: mail,
      firstName: name,
      lastName: lastName,
      phoneNumber: phoneNumber,
      companyName: inheritedCompanyName,
      languages: mainWriterLanguages.concat(minorWriterLanguages),
      thematics: writerThematics,
      role: ROLES.GLOBAL_WRITER,
      isoLanguageCode: selectedLanguage.isoCode,
    };

    axiosPrivate
      .post(API.createWriter, body)
      .then((res) => {
        dispatch(addUser({ ...res.data, role: ROLES.GLOBAL_WRITER }));
        setIsSuccess(true);
        setErrorSuccessMsg(
          `${t('Le rédacteur')} ${mail} ${t('a été créé avec succès')}`
        );

        setErrorSuccess(true);
      })
      .catch((e) => {
        setIsSuccess(false);
        switch (e.response.status) {
          case 409:
            setErrorSuccessMsg(
              `${t("Un utilisateur existe déjà avec l'email")} ${mail}`
            );
            break;
          default:
            setErrorSuccessMsg(t("Oups....! Une erreur s'est produite"));
            break;
        }
        setErrorSuccess(true);
      });
  };

  // ── EDIT MODAL ──
  if (props.user)
    return (
      <div>
        <Modal centered contentClassName="um-modal-content" isOpen={props.open}>
          {/* Header */}
          <div className="um-header">
            <h5 className="um-title">{t('Editer le profil')}</h5>
            <button className="um-btn-close" onClick={props.handleClose} aria-label="Close">
              <svg width="16" height="16" viewBox="0 0 16 16" fill="none">
                <path d="M12 4L4 12M4 4l8 8" stroke="#616161" strokeWidth="1.5" strokeLinecap="round" />
              </svg>
            </button>
          </div>

          {/* Body */}
          <div className="um-body">
            {/* Row 1 — Prénom + Nom */}
            <div className="um-row">
              <div className="um-field">
                <span className="um-label">{t('Prénom')} *</span>
                <input
                  value={name}
                  onChange={(e) => setName(e.target.value)}
                  type="text"
                  className="um-input"
                />
              </div>
              <div className="um-field">
                <span className="um-label">{t('Nom')} *</span>
                <input
                  value={lastName}
                  onChange={(e) => setLastName(e.target.value)}
                  type="text"
                  className="um-input"
                />
              </div>
            </div>

            {/* Row 2 — Email */}
            <div className="um-row-full">
              <div className="um-field">
                <span className="um-label">{t('E-mail')} *</span>
                <input
                  value={mail}
                  onChange={(e) => setMail(e.target.value)}
                  type="text"
                  className="um-input"
                />
              </div>
            </div>

            {/* Row 3 — Téléphone */}
            <div className="um-row-full">
              <div className="um-field">
                <span className="um-label">{t('Téléphone')}</span>
                <input
                  type="text"
                  value={phoneNumber}
                  onChange={(e) => {
                    if (e.target.value === '' || PHONENUMBER_REGEX.test(e.target.value)) {
                      setPhoneNumber(e.target.value);
                    }
                  }}
                  className="um-input"
                />
              </div>
            </div>

            {/* Row 4 — Langue + Mot de passe */}
            <div className="um-row">
              <div className="um-field">
                <LanguageSelector
                  className="um-label"
                  selectedLanguage={selectedLanguage}
                  onLanguageChange={(option) => setSelectedLanguage(option)}
                />
              </div>
              <div className="um-field">
                <span className="um-label">{t('Mot de passe')} *</span>
                <div className="um-input-password-wrapper">
                  <input
                    type={showPassword ? 'text' : 'password'}
                    className="um-input"
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                  />
                  <button
                    type="button"
                    className="um-password-toggle"
                    onClick={() => setShowPassword(!showPassword)}
                  >
                    {showPassword ? (
                      <img src="/Images/icon-eye-black.png" alt="eye" />
                    ) : (
                      <img src="/Images/icon-eye-gray.png" alt="eye" />
                    )}
                  </button>
                </div>
                <p className="um-hint">
                  * {t('Avoir au moins 8 caractères avec une lettre majuscule, un chiffre, un symbole et ne pas être identique au nom.')}
                </p>
              </div>
            </div>

            {/* Row 5 — Thématiques */}
            <div className="um-row-full">
              <div className="um-field">
                <span className="um-label">{t('Thématiques')}</span>
                <CustomChipsAutocomplete
                  callbackFromMultiSelect={callbackFromThematicMultiSelect}
                  value={writerThematics}
                  disabled={!availableThematicsList}
                  list={availableThematicsList}
                  setValue={(option) => setWriterThematics(option)}
                  getOptionLabel={(option) => {
                    if (!option) return '';
                    if (typeof option === 'string') return option;
                    return t(option.thematic || option);
                  }}
                />
              </div>
            </div>

            {/* Row 6 — Langues de rédaction */}
            <div className="um-row-full">
              <div className="um-field">
                <span className="um-label mb-2">{t('Langues de rédaction :')}</span>
                <div className="box-wrapper-cols">
                  {MAIN_LANGUAGES.slice(0, 3).map((language, key) => (
                    <div key={key} className="cols">
                      <div className="box-checkbox box-list-check">
                        <input
                          checked={findLanguageIndex(language) > -1}
                          onChange={() => checkLanguage(language)}
                          className="form-check-input"
                          type="checkbox"
                        />
                        <div style={{
                          fontSize: '12px',
                          fontWeight: 400,
                          fontFamily: 'Poppins, sans-serif',
                          color: '#1A1A1A'
                        }}> {t(language)}</div>
                      </div>
                    </div>
                  ))}
                </div>

                <div className="box-wrapper-cols mt-4">
                  {MAIN_LANGUAGES.slice(3).map((language, key) => (
                    <div key={key} className="cols">
                      <div className="box-checkbox box-list-check">
                        <input
                          checked={findLanguageIndex(language) > -1}
                          onChange={() => checkLanguage(language)}
                          className="form-check-input"
                          type="checkbox"
                        />
                        <div style={{
                          fontSize: '12px',
                          fontWeight: 400,
                          fontFamily: 'Poppins, sans-serif',
                          color: '#1A1A1A'
                        }}>{t(language)}</div>
                      </div>
                    </div>
                  ))}
                  <div className="cols">
                    <CustomAutocompleteMultipleChoice
                      callbackFromMultiSelect={callbackFromLanguagesMultiSelect}
                      value={minorWriterLanguages}
                      disabled={!availableLanguagesList}
                      list={availableLanguagesList.filter(
                        (x) =>
                          !MAIN_LANGUAGES.map((lan) => lower(lan)).includes(
                            lower(x.language)
                          )
                      )}
                      setValue={(option) => setMinorWriterLanguages(option)}
                      getOptionLabel={(option) => {
                        return t(option.language);
                      }}
                    />
                  </div>
                </div>
              </div>
            </div>
          </div>

          {/* Footer */}
          <div className="um-footer">
            <button className="um-btn-cancel" onClick={props.handleClose}>
              {t('Annuler')}
            </button>
            <button
              className="um-btn-save"
              onClick={updateWriter}
              disabled={disabledForm}
            >
              {t('Sauvegarder')}
            </button>
          </div>
        </Modal>

        <ErrorSuccesModal
          open={errorSuccess}
          message={errorSuccessMsg}
          handleClose={() => {
            if (isSuccess === true) props.handleClose();
            setErrorSuccess(false);
          }}
          success={isSuccess}
          toggle={() => setErrorSuccess(!errorSuccess)}
        />
      </div>
    );

  // ── CREATE MODAL ──
  return (
    <div>
      <Modal centered contentClassName="um-modal-content" isOpen={props.open}>
        {/* Header */}
        <div className="um-header">
          <h5 className="um-title">{t('Ajouter un rédacteur')}</h5>
          <button className="um-btn-close" onClick={props.handleClose} aria-label="Close">
            <svg width="16" height="16" viewBox="0 0 16 16" fill="none">
              <path d="M12 4L4 12M4 4l8 8" stroke="#616161" strokeWidth="1.5" strokeLinecap="round" />
            </svg>
          </button>
        </div>

        {/* Body */}
        <div className="um-body">
          {/* Row 1 — Prénom + Nom */}
          <div className="um-row">
            <div className="um-field">
              <span className="um-label">{t('Prénom')} *</span>
              <input
                value={name}
                onChange={(e) => setName(e.target.value)}
                type="text"
                className="um-input"
              />
            </div>
            <div className="um-field">
              <span className="um-label">{t('Nom')} *</span>
              <input
                value={lastName}
                onChange={(e) => setLastName(e.target.value)}
                type="text"
                className="um-input"
              />
            </div>
          </div>

          {/* Row 2 — Email */}
          <div className="um-row-full">
            <div className="um-field">
              <span className="um-label">{t('E-mail')} *</span>
              <input
                value={mail}
                onChange={(e) => setMail(e.target.value)}
                type="text"
                className="um-input"
              />
            </div>
          </div>

          {/* Row 3 — Téléphone */}
          <div className="um-row-full">
            <div className="um-field">
              <span className="um-label">{t('Téléphone')}</span>
              <input
                type="text"
                value={phoneNumber}
                onChange={(e) => {
                  if (e.target.value === '' || PHONENUMBER_REGEX.test(e.target.value)) {
                    setPhoneNumber(e.target.value);
                  }
                }}
                className="um-input"
              />
            </div>
          </div>

          {/* Row 4 — Langue */}
          <div className="um-row-full">
            <div className="um-field">
              <LanguageSelector
                className="um-label"
                selectedLanguage={selectedLanguage}
                onLanguageChange={(option) => setSelectedLanguage(option)}
              />
            </div>
          </div>

          {/* Row 5 — Thématiques */}
          <div className="um-row-full">
            <div className="um-field">
              <span className="um-label">{t('Thématiques')}</span>
              <CustomChipsAutocomplete
                callbackFromMultiSelect={callbackFromThematicMultiSelect}
                value={writerThematics}
                disabled={!availableThematicsList}
                list={availableThematicsList}
                setValue={(option) => setWriterThematics(option)}
                getOptionLabel={(option) => {
                  if (!option) return '';
                  if (typeof option === 'string') return option;
                  return t(option.thematic || option);
                }}
              />
            </div>
          </div>

          {/* Row 6 — Langues de rédaction */}
          <div className="um-row-full">
            <div className="um-field">
              <span className="um-label mb-2">{t('Langues de rédaction :')}</span>
              <div className="box-wrapper-cols">
                {MAIN_LANGUAGES.slice(0, 3).map((language, key) => (
                  <div key={key} className="cols">
                    <div className="box-checkbox box-list-check">
                      <input
                        checked={findLanguageIndex(language) > -1}
                        onChange={() => checkLanguage(language)}
                        className="form-check-input"
                        type="checkbox"
                      />
                      <div style={{
                        fontSize: '12px',
                        fontWeight: 400,
                        fontFamily: 'Poppins, sans-serif',
                        color: '#1A1A1A'
                      }}> {t(language)}</div>
                    </div>
                  </div>
                ))}
              </div>

              <div className="box-wrapper-cols mt-4">
                {MAIN_LANGUAGES.slice(3).map((language, key) => (
                  <div key={key} className="cols">
                    <div className="box-checkbox box-list-check">
                      <input
                        checked={findLanguageIndex(language) > -1}
                        onChange={() => checkLanguage(language)}
                        className="form-check-input"
                        type="checkbox"
                      />
                      <div style={{
                        fontSize: '12px',
                        fontWeight: 400,
                        fontFamily: 'Poppins, sans-serif',
                        color: '#1A1A1A'
                      }}> {t(language)}</div>
                    </div>
                  </div>
                ))}
                <div className="cols">
                  <CustomAutocompleteMultipleChoice
                    callbackFromMultiSelect={callbackFromLanguagesMultiSelect}
                    value={minorWriterLanguages}
                    disabled={!availableLanguagesList}
                    list={availableLanguagesList.filter(
                      (x) =>
                        !MAIN_LANGUAGES.map((lan) => lower(lan)).includes(
                          lower(x.language)
                        )
                    )}
                    setValue={(option) => setMinorWriterLanguages(option)}
                    getOptionLabel={(option) => {
                      return t(option.language);
                    }}
                  />
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Footer */}
        <div className="um-footer">
          <button className="um-btn-cancel" onClick={props.handleClose}>
            {t('Annuler')}
          </button>
          <button
            className="um-btn-save"
            onClick={createWriter}
            disabled={disabledForm}
          >
            {t('Sauvegarder')}
          </button>
        </div>
      </Modal>

      <ErrorSuccesModal
        open={errorSuccess}
        message={errorSuccessMsg}
        handleClose={() => {
          if (isSuccess === true) props.handleClose();
          setErrorSuccess(false);
        }}
        success={isSuccess}
        toggle={() => setErrorSuccess(!errorSuccess)}
      />
    </div>
  );
};

export default GlobalWriterModal;